import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../../services/api';
import ProductCard from '../../components/ProductCard/ProductCard';
import { useCart } from '../../context/CartContext';
import { toast } from 'react-toastify';
import AddToCartModal from '../../components/AddToCartModal/AddToCartModal';
import './SearchPage.css';

function useQuery() {
    return new URLSearchParams(useLocation().search);
}

const SkeletonCard = () => (
    <div className="skeleton-card">
        <div className="skeleton-image"></div>
        <div className="skeleton-text"></div>
        <div className="skeleton-text short"></div>
    </div>
);

const PAGE_LIMIT = 30;

const SearchPage = () => {
    const query = useQuery();
    const searchQuery = query.get('q');
    const jenisQuery = query.get('jenisId');

    const [barangList, setBarangList] = useState([]);
    const [resultCount, setResultCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    
    const { addToCart } = useCart();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const isMobile = window.innerWidth < 768;
    
    const API_URL = api.defaults.baseURL.replace('/api', '');

    const fetchSearchResults = useCallback(async (pageToFetch, isNewSearch = false) => {
        setLoading(true);
        try {
            const { data } = await api.get('/barang/customer', {
                params: {
                    page: pageToFetch,
                    limit: PAGE_LIMIT,
                    search: searchQuery,
                    jenisId: jenisQuery || undefined
                }
            });
            
            const newBarang = data.data || [];
            if (isNewSearch) {
                setBarangList(newBarang);
            } else {
                setBarangList(prev => {
                    const existingIds = new Set(prev.map(b => b.id));
                    const filteredNewBarang = newBarang.filter(b => !existingIds.has(b.id));
                    return [...prev, ...filteredNewBarang];
                });
            }
            
            setPage(pageToFetch + 1);
            setHasMore(data.currentPage < data.totalPages);
            if (isNewSearch) {
                setResultCount(data.totalItems || 0);
            }
        } catch (error) {
            console.error("Gagal melakukan pencarian:", error);
            toast.error("Gagal memuat hasil pencarian.");
        } finally {
            setLoading(false);
        }
    }, [searchQuery, jenisQuery]);

    useEffect(() => {
        setBarangList([]);
        setPage(1);
        setHasMore(true);
        fetchSearchResults(1, true);
    }, [searchQuery, jenisQuery, fetchSearchResults]);

    const handleScroll = useCallback(() => {
        const bottom = Math.ceil(window.innerHeight + window.scrollY) >= document.documentElement.scrollHeight - 300;
        if (bottom && hasMore && !loading) {
            fetchSearchResults(page, false);
        }
    }, [loading, hasMore, page, fetchSearchResults]);

    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [handleScroll]);

    const handleAddToCartClick = (barang) => {
        const fetchDetailPromise = async () => {
            // ====================================================================
            // PERBAIKAN UTAMA: Menggunakan endpoint `/barang/customer/:id` yang BENAR
            // ====================================================================
            const { data: detailBarang } = await api.get(`/barang/customer/${barang.id}`);

            if (!detailBarang || !detailBarang.Products || detailBarang.Products.length === 0) {
                throw new Error("Varian untuk produk ini tidak tersedia.");
            }

            if (isMobile) {
                setSelectedProduct(detailBarang);
                setIsModalOpen(true);
                return `Pilih varian untuk ${detailBarang.nama_barang}`;
            } else {
                const defaultVariant = detailBarang.Products[0];
                addToCart(defaultVariant.id, 1);
                return `${detailBarang.nama_barang} (${defaultVariant.nama_satuan}) ditambahkan!`;
            }
        };

        toast.promise(
            fetchDetailPromise(),
            {
                pending: 'Memuat detail produk...',
                success: { render: ({ data }) => data },
                error: { render: ({ data }) => data.message || "Gagal mengambil detail produk." }
            }
        );
    };
    
    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedProduct(null);
    };

    // ... sisa kode (renderContent, return, etc.) tetap sama ...
    const renderContent = () => {
        if (loading && page === 1) {
            return (
                <div className="product-grid">
                    {Array.from({ length: 12 }).map((_, index) => <SkeletonCard key={index} />)}
                </div>
            );
        }
        if (barangList.length > 0) {
            return (
                <div className="product-grid">
                    {barangList.map(barang => (
                        <ProductCard 
                            key={barang.id} 
                            barang={barang} 
                            API_URL={API_URL}
                            onAddToCartClick={handleAddToCartClick}
                        />
                    ))}
                </div>
            );
        }
        if (!loading && barangList.length === 0) {
            return (
                <div className="no-results-container">
                    <h3>Produk Tidak Ditemukan</h3>
                    <p>Coba gunakan kata kunci lain untuk menemukan produk yang Anda cari.</p>
                </div>
            );
        }
        return null;
    };

    return (
        <div className="search-page-container">
            <div className="search-header">
                <h1>Hasil Pencarian untuk: <span>"{searchQuery}"</span></h1>
                {resultCount > 0 && <p>{resultCount} produk ditemukan</p>}
            </div>

            {renderContent()}

            {loading && page > 1 && <p className="loading-text">Memuat lebih banyak...</p>}
            {!hasMore && barangList.length > 0 && <p className="loading-text">Semua produk sudah ditampilkan.</p>}

            {isModalOpen && (
                <AddToCartModal
                    barang={selectedProduct}
                    onClose={handleCloseModal}
                />
            )}
        </div>
    );
};

export default SearchPage;